import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import api from "@/lib/api";
import { CheckCircle2, CalendarDays, Clock } from "lucide-react";

function nextWeekdays(n = 10) {
  const days = [];
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (days.length < n) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) {
      days.push(new Date(d));
    }
    d.setDate(d.getDate() + 1);
  }
  return days;
}

const HOURS = ["09:00", "10:00", "11:00", "13:00", "14:00", "15:00", "16:00"];

export default function AppointmentDialog({ open, onOpenChange, defaultType = "decouverte" }) {
  const [step, setStep] = useState(1);
  const [type, setType] = useState(defaultType);
  const [date, setDate] = useState(null);
  const [heure, setHeure] = useState(null);
  const [form, setForm] = useState({ nom: "", courriel: "", telephone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const days = nextWeekdays(10);

  const reset = () => {
    setStep(1); setDate(null); setHeure(null);
    setForm({ nom: "", courriel: "", telephone: "", message: "" });
    setSuccess(false);
  };

  const close = (v) => {
    if (!v) setTimeout(reset, 300);
    onOpenChange(v);
  };

  const submit = async () => {
    if (!form.nom || !form.courriel || !date || !heure) {
      toast.error("Veuillez compléter tous les champs requis.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/appointments", {
        ...form,
        type_rdv: type,
        date: date.toISOString().split("T")[0],
        heure,
      });
      setSuccess(true);
      toast.success("Rendez-vous confirmé ! Vous recevrez un courriel de confirmation.");
    } catch (e) {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl" data-testid="appointment-dialog">
        {success ? (
          <div className="text-center py-8" data-testid="appointment-success">
            <CheckCircle2 className="h-16 w-16 text-[#00B8D9] mx-auto mb-4" />
            <h3 className="font-heading text-2xl font-bold text-[#0B1F3A] mb-2">Rendez-vous confirmé !</h3>
            <p className="text-[#4B5563]">
              Un courriel de confirmation a été envoyé à <b>{form.courriel}</b>.<br />
              Date : <b>{date?.toLocaleDateString("fr-CA")}</b> à <b>{heure}</b>
            </p>
            <Button onClick={() => close(false)} className="mt-6 bg-[#0B1F3A] hover:bg-[#0B1F3A]/90" data-testid="appointment-close-success">
              Fermer
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-[#0B1F3A]">Réservez votre rendez-vous</DialogTitle>
              <DialogDescription>Étape {step} / 3 — Sans engagement, 100 % gratuit.</DialogDescription>
            </DialogHeader>

            {step === 1 && (
              <div className="space-y-4">
                <Label className="text-[#0B1F3A] font-semibold">Type de rendez-vous</Label>
                <RadioGroup value={type} onValueChange={setType} className="grid sm:grid-cols-2 gap-3">
                  <label className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${type === "decouverte" ? "border-[#00B8D9] bg-[#00B8D9]/5" : "border-gray-200"}`}>
                    <RadioGroupItem value="decouverte" data-testid="appt-type-decouverte" />
                    <div>
                      <div className="font-semibold text-[#0B1F3A]">Appel découverte</div>
                      <div className="text-xs text-[#4B5563]">30 min · Pour comprendre vos besoins</div>
                    </div>
                  </label>
                  <label className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${type === "diagnostic" ? "border-[#00B8D9] bg-[#00B8D9]/5" : "border-gray-200"}`}>
                    <RadioGroupItem value="diagnostic" data-testid="appt-type-diagnostic" />
                    <div>
                      <div className="font-semibold text-[#0B1F3A]">Diagnostic numérique</div>
                      <div className="text-xs text-[#4B5563]">45 min · Audit complet de votre présence</div>
                    </div>
                  </label>
                </RadioGroup>
                <Button onClick={() => setStep(2)} className="w-full bg-[#00B8D9] hover:bg-[#0096B3]" data-testid="appt-step1-next">
                  Continuer
                </Button>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div>
                  <Label className="text-[#0B1F3A] font-semibold flex items-center gap-2"><CalendarDays className="h-4 w-4" /> Choisissez une date</Label>
                  <div className="grid grid-cols-5 gap-2 mt-2">
                    {days.map((d) => (
                      <button
                        key={d.toISOString()}
                        onClick={() => setDate(d)}
                        data-testid={`appt-date-${d.toISOString().split("T")[0]}`}
                        className={`p-2 rounded-md text-sm border-2 transition-all ${date?.toDateString() === d.toDateString() ? "border-[#00B8D9] bg-[#00B8D9] text-white" : "border-gray-200 hover:border-[#00B8D9]"}`}
                      >
                        <div className="text-[10px] uppercase">{d.toLocaleDateString("fr-CA", { weekday: "short" })}</div>
                        <div className="font-bold">{d.getDate()}</div>
                        <div className="text-[10px]">{d.toLocaleDateString("fr-CA", { month: "short" })}</div>
                      </button>
                    ))}
                  </div>
                </div>
                {date && (
                  <div>
                    <Label className="text-[#0B1F3A] font-semibold flex items-center gap-2"><Clock className="h-4 w-4" /> Heure</Label>
                    <div className="grid grid-cols-4 gap-2 mt-2">
                      {HOURS.map((h) => (
                        <button
                          key={h}
                          onClick={() => setHeure(h)}
                          data-testid={`appt-hour-${h}`}
                          className={`p-2 rounded-md text-sm border-2 transition-all ${heure === h ? "border-[#00B8D9] bg-[#00B8D9] text-white" : "border-gray-200 hover:border-[#00B8D9]"}`}
                        >{h}</button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep(1)} className="flex-1" data-testid="appt-step2-back">Retour</Button>
                  <Button onClick={() => setStep(3)} disabled={!date || !heure} className="flex-1 bg-[#00B8D9] hover:bg-[#0096B3]" data-testid="appt-step2-next">Continuer</Button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-3">
                <div><Label>Nom complet *</Label><Input data-testid="appt-input-nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} /></div>
                <div><Label>Courriel *</Label><Input data-testid="appt-input-courriel" type="email" value={form.courriel} onChange={(e) => setForm({ ...form, courriel: e.target.value })} /></div>
                <div><Label>Téléphone</Label><Input data-testid="appt-input-tel" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} /></div>
                <div><Label>Message (optionnel)</Label><Textarea data-testid="appt-input-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></div>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => setStep(2)} className="flex-1" data-testid="appt-step3-back">Retour</Button>
                  <Button onClick={submit} disabled={loading} className="flex-1 bg-[#00B8D9] hover:bg-[#0096B3]" data-testid="appt-submit">
                    {loading ? "Réservation..." : "Confirmer le RDV"}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
